import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Loader2, Folder, Download, FileText, Search, Upload, CheckCircle2, XCircle, Filter, Eye, AlertTriangle } from "lucide-react";
import AppShell from "../components/app/AppShell";
import PageHeader, { EmptyState } from "../components/app/PageHeader";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";

const STATUS_BADGE = {
  pending_review: "badge-pending",
  approved: "badge-verified",
  rejected: "badge-failed",
};
const STATUS_LABEL = {
  pending_review: "Pending review",
  approved: "Approved",
  rejected: "Rejected",
};
const TYPE_LABELS = {
  "application/pdf": "PDF",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "image/jpeg": "JPG", "image/png": "PNG", "image/webp": "WEBP", "image/heic": "HEIC",
  "text/plain": "TXT",
};

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

export default function DocumentLibrary() {
  const { user } = useAuth();
  const canManage = ["firm_admin", "compliance_manager"].includes(user?.role);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "", mime: "" });
  const [reviewing, setReviewing] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.q) p.set("q", filters.q);
      if (filters.status) p.set("status", filters.status);
      if (filters.mime) p.set("mime", filters.mime);
      const { data } = await api.get(`/api/documents?${p}`);
      setDocs(data.items);
    } catch (e) { alert(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filters.status, filters.mime]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("purpose", "firm_upload");
      await api.post("/api/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      track(EVT.document_uploaded, { size: file.size, type: file.type });
      await load();
    } catch (e) { alert(formatApiError(e)); }
    finally { setUploading(false); if (fileInput.current) fileInput.current.value = ""; }
  };

  const download = async (d) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${base}/api/documents/${d.id}/download`, { credentials: "include" });
    if (!res.ok) { alert("Download failed"); return; }
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = d.filename; a.click();
    URL.revokeObjectURL(a.href);
  };

  const review = async (d, status) => {
    try {
      await api.patch(`/api/documents/${d.id}/review`, { status, note: reviewNote || null });
      setReviewing(null); setReviewNote("");
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <AppShell>
      <PageHeader
        title="Document Library"
        subtitle="Every document uploaded across your onboarding flows — encrypted at rest with your firm key."
        actions={
          <>
            <input ref={fileInput} type="file" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            <button onClick={() => fileInput.current?.click()} disabled={uploading} className="btn btn-primary" data-testid="docs-upload">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? "Uploading…" : "Upload document"}
            </button>
          </>
        }
      />

      <div className="card2 p-4 mb-5 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate2" />
          <input className="input2 pl-9" placeholder="Search filenames…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="docs-search" />
        </div>
        <select className="input2 md:w-44" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="docs-status">
          <option value="">All statuses</option>
          <option value="pending_review">Pending review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select className="input2 md:w-44" value={filters.mime} onChange={(e) => setFilters({ ...filters, mime: e.target.value })} data-testid="docs-mime">
          <option value="">All types</option>
          <option value="application/pdf">PDF</option>
          <option value="application/vnd.openxmlformats-officedocument.wordprocessingml.document">DOCX</option>
          <option value="image/jpeg">JPG</option>
          <option value="image/png">PNG</option>
        </select>
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-16" />)}</div>
      ) : docs.length === 0 ? (
        <EmptyState icon={Folder} title="No documents yet" description="Documents uploaded by clients in onboarding flows will appear here. You can also upload firm-wide documents using the button above." />
      ) : (
        <>
          {/* Desktop table */}
          <div className="card2 overflow-hidden hidden md:block">
            <table className="w-full">
              <thead className="bg-surface border-b border-border2 text-left">
                <tr>
                  <th className="px-5 py-3 t-label text-slate2">File</th>
                  <th className="px-5 py-3 t-label text-slate2">Client</th>
                  <th className="px-5 py-3 t-label text-slate2">Type</th>
                  <th className="px-5 py-3 t-label text-slate2">Size</th>
                  <th className="px-5 py-3 t-label text-slate2">Uploaded</th>
                  <th className="px-5 py-3 t-label text-slate2">Status</th>
                  <th className="px-5 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border2">
                {docs.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                    <td className="px-5 py-3 t-body-sm font-medium flex items-center gap-2"><FileText size={14} className="text-slate2 flex-shrink-0" />{d.filename}</td>
                    <td className="px-5 py-3 t-body-sm">{d.client_name ? <Link to={`/app/clients/${d.client_id}`} className="text-indigo2 hover:underline">{d.client_name}</Link> : <span className="text-slate2">—</span>}</td>
                    <td className="px-5 py-3 t-caption t-mono">{TYPE_LABELS[d.mime] || d.mime}</td>
                    <td className="px-5 py-3 t-caption">{fmtBytes(d.size)}</td>
                    <td className="px-5 py-3 t-caption">{new Date(d.uploaded_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3"><span className={`badge2 ${STATUS_BADGE[d.status] || "badge-locked"}`}>{STATUS_LABEL[d.status] || d.status}</span></td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => download(d)} className="btn btn-ghost btn-sm" data-testid={`download-${d.id}`}><Download size={14} /></button>
                      {canManage && (
                        <button onClick={() => setReviewing(d)} className="btn btn-ghost btn-sm ml-1" data-testid={`review-${d.id}`}><Eye size={14} />Review</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {docs.map((d) => (
              <div key={d.id} className="card2 p-4" data-testid={`doc-card-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="t-body-sm font-medium truncate flex items-center gap-1.5"><FileText size={14} className="text-slate2 flex-shrink-0" />{d.filename}</div>
                    <div className="t-caption mt-1">{d.client_name || "—"} · {TYPE_LABELS[d.mime] || d.mime} · {fmtBytes(d.size)}</div>
                  </div>
                  <span className={`badge2 ${STATUS_BADGE[d.status] || "badge-locked"} flex-shrink-0`}>{STATUS_LABEL[d.status] || d.status}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => download(d)} className="btn btn-ghost btn-sm flex-1"><Download size={14} />Download</button>
                  {canManage && <button onClick={() => setReviewing(d)} className="btn btn-secondary btn-sm flex-1"><Eye size={14} />Review</button>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {reviewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setReviewing(null)}>
          <div className="card2 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
            <h3 className="t-h3 mb-2">{reviewing.filename}</h3>
            <p className="t-body-sm text-slate2 mb-3">{reviewing.client_name || "Firm document"} · {fmtBytes(reviewing.size)}</p>
            <textarea className="input2 w-full" rows={3} placeholder="Review note (optional)" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} data-testid="review-note" />
            <div className="flex flex-wrap gap-2 mt-4 justify-end">
              <button onClick={() => setReviewing(null)} className="btn btn-secondary">Cancel</button>
              <button onClick={() => review(reviewing, "rejected")} className="btn btn-danger" data-testid="review-reject"><XCircle size={14} />Reject</button>
              <button onClick={() => review(reviewing, "approved")} className="btn btn-primary" data-testid="review-approve"><CheckCircle2 size={14} />Approve</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
